import getRecaptchaToken from '../helpers/getRecaptchaToken';
import AppContext from './context/AppContext';
import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import StarRating from './StarRating';
import '../styles/ProductAddReviewForm.css';

const MAX_STARS = 5;
/**
 * Component for adding reviews to a product.
 * @component
 */
const ProductReviewsForm = ({ productId }) => {
    const { appContext } = useContext(AppContext);
    /** States */
    const [ feedback, setFeedback ] = useState('');
    const [ feedbackType, setFeedbackType ] = useState('none');
    const [ comment, setComment ] = useState('');
    const [ title, setTitle ] = useState('');
    const [ rating, setRating ] = useState(0);

    /** Form Event Handlers */
    /**
     * Change the body.
     * @param {Object} e The event object.
     */
    const onChangeComment = (e) => setComment(e.target.value);

    /**
     * Change the title.
     * @param {Object} e The event object.
     */
    const onChangeTitle = (e) => setTitle(e.target.value);

    /**
     * Submit the review.
     * @param {Object} e The event object.
     */
    const onSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        submitReview();
    };

    /**
     * Submit a review.
     */
    const submitReview = async () => {
        try {
            if (rating <= 0 || rating > MAX_STARS) {
                setFeedback('Starts rating is required.');
                setFeedbackType('error');
                return;
            }
            const url = new URL('/api/reviews', process.env.APP_URL);
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    title,
                    comment,
                    productId,
                    starsRating: rating,
                    token: await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                }),
            });
            if (!response.ok) {
                throw new Error('Review submission failed: bad API response.');
            }
            const data = await response.json();
            if (!data.review) {
                throw new Error('Data received is not as expected.');
            }
            // Reset form after succesful request.
            setRating(0);
            setComment('');
            setTitle('');
            setFeedback('Your review has been successfully submitted.');
            setFeedbackType('success');
        } catch (e) {
            console.error(e);
            setFeedback('Review submission failed. Please try again later.');
            setFeedbackType('error');
        }
    };

    /** UI */
    return <Form
        className={'add-review-form'}
        onSubmit={onSubmit}>
        <h3>Write a Review</h3>
        <Form.Group
            className={'mb-3'}
            controlId={'addReviewForm.rating'}>
            <Form.Label>Rating</Form.Label>
            <StarRating
                clickable={true}
                rating={rating}
                setRating={setRating}
                size={'2rem'}
            />
        </Form.Group>
        <Form.Group
            className={'mb-3'}
            controlId={'addReviewForm.title'}>
            <Form.Label>Title</Form.Label>
            <Form.Control
                type={'text'}
                value={title}
                onChange={onChangeTitle}
                required
            />
        </Form.Group>
        <Form.Group
            className={'mb-3'}
            controlId={'addReviewForm.body'}>
            <Form.Label>Comment</Form.Label>
            <Form.Control
                as={'textarea'}
                value={comment}
                onChange={onChangeComment}
                required
            />
        </Form.Group>
        <p
            className={`product-reviews--feedback-${feedbackType}`}>
            {feedback}
        </p>
        <button
            className={'add-review-form--submit-button'}
            type={'submit'}>
            Submit Review
        </button>
    </Form>;
};

/** Data Types of Properties */
ProductReviewsForm.propTypes = {
    /** The product ID. */
    productId: PropTypes.number.isRequired,
};

export default ProductReviewsForm;
